/**
 * Shared, reference-counted subscription to the desktop `AUDIO_LEVELS` event.
 *
 * `AUDIO_LEVELS` is high-frequency visualizer data (~30 FPS, broadcast to every
 * webview). Registering the listener at module load meant every Tauri window
 * ran the merge/throttle/atom-write pipeline for the whole life of the app,
 * including the windows that render no bars at all. Instead, subscription
 * lifetime is owned by the components and hooks that actually read
 * `tauriAudioLevelsAtom`: the listener exists only while at least one of them is
 * mounted, and window scoping falls out of that for free — a webview that never
 * mounts a consumer never registers a listener.
 *
 * Prefer `useAudioLevelsSubscription` from React; this module is the imperative
 * core underneath it.
 */

import { tauriAudioLevelsAtom } from '@/atoms/tauriAudioLevelsAtom';
import { logger } from '@/utils/logger/logger';
import { getDefaultStore } from 'jotai';
import { throttle } from 'lodash-es';
import { isTauri } from '../utils';
import { type ManagedUnlistenFn } from './managed-listen';

export const AUDIO_LEVELS_EVENT = 'AUDIO_LEVELS';

export type AudioLevelsEventPayload = {
  levels: number[];
  source?: string;
};

/** Latest native level set. Readers that only sample the atom live here. */
export const readAudioLevels = () => getDefaultStore().get(tauriAudioLevelsAtom);

/** Bands per frame — the atom's contract with every bar renderer. */
const BAND_COUNT = 5;

/** ~30 FPS. Matches the emitter's cadence; anything faster is wasted work. */
const THROTTLE_MS = 33;

const SOURCE_UNKNOWN = 'unknown';

/**
 * Backoff before each retry of a rejected `listen()`, and by its length the
 * attempt budget (initial try + one per entry). Bounded on purpose: a denial
 * that is permanent, such as a webview without the event permission, must not
 * turn into an endless wakeup loop, which is the cost this module exists to
 * remove.
 */
const REGISTRATION_RETRY_BACKOFF_MS = [250, 1000];

let subscriberCount = 0;
let unlisten: ManagedUnlistenFn | null = null;
/**
 * The handle from the last release, kept for exactly one more attempt.
 *
 * `managedListen`'s release function retries a denied `plugin:event|unlisten`
 * on a SECOND call — but only for a caller that still holds it, and this module
 * used to drop the handle in the same statement that invoked it. So a webview
 * whose unlisten is transiently denied leaked its Rust listener for the life of
 * the document, with the one affordance that could have reclaimed it thrown
 * away. This is a genuinely available second chance here specifically:
 * `AUDIO_LEVELS` is reference-counted and re-subscribed on every consumer
 * mount, so a later mount arrives to spend it.
 *
 * A repeat call is safe without knowing which happened, which is why the call
 * site does not need to: after a successful release `managedListen` memoizes
 * the settled promise and hands the same one back, so a second call costs no
 * IPC. Cleared when spent, so this stays one extra attempt and not a retry loop
 * — `MAX_RELEASE_ATTEMPTS` bounds the far side to the same two.
 */
let staleRelease: ManagedUnlistenFn | null = null;
let registrationInFlight = false;
let latestBySource: Record<string, number[]> = {};
let flush: ReturnType<typeof createFlush> | null = null;

/**
 * Merge the most recent frame from every source and publish it.
 *
 * Mic and speaker arrive as separate events, so taking the per-band max keeps
 * one source from overwriting the other between frames.
 */
function createFlush() {
  return throttle(
    () => {
      const frames = Object.values(latestBySource);
      if (frames.length === 0) return;
      const combined = Array.from({ length: BAND_COUNT }, (_, band) =>
        Math.max(...frames.map((frame) => frame[band] ?? 0))
      );
      getDefaultStore().set(tauriAudioLevelsAtom, combined);
    },
    THROTTLE_MS,
    { leading: true, trailing: true }
  );
}

/**
 * Tolerates being called after `teardown()` on purpose: the managed listener
 * removes the Rust-side listener before it unregisters the callback, so one
 * more frame can arrive after release. `flush` is null by then and
 * `latestBySource` is rebuilt on the next claim, so a late frame is recorded
 * into a map nothing reads and paints nothing.
 */
function handleEvent(event: { payload?: AudioLevelsEventPayload }): void {
  const payload = event?.payload;
  const levels = payload?.levels;
  if (!Array.isArray(levels) || levels.length !== BAND_COUNT) return;

  const source = payload?.source;
  latestBySource[
    typeof source === 'string' && source.length > 0 ? source : SOURCE_UNKNOWN
  ] = levels;
  flush?.();
}

function register(): void {
  registrationInFlight = true;
  void (async () => {
    try {
      // Dynamic on purpose, as the plain `listen()` import was: it keeps
      // `@tauri-apps/api` out of the bundle every non-desktop build ships.
      const { managedListen } = await import('./managed-listen');

      for (let attempt = 0; ; attempt += 1) {
        // Released while we were backing off; nothing wants levels any more.
        if (subscriberCount === 0) return;

        try {
          const fn = await managedListen<AudioLevelsEventPayload>(
            AUDIO_LEVELS_EVENT,
            handleEvent
          );
          if (subscriberCount === 0) {
            // Released during the in-flight registration. `teardown()` already
            // ran with no handle to cancel, so undo the registration here
            // rather than leaving an ownerless listener for the rest of the
            // session. Through `releaseHandle` for the same reason `teardown()`
            // is: an undo that is itself denied is the leak, not the fix.
            releaseHandle(fn);
            return;
          }
          unlisten = fn;
          return;
        } catch (error) {
          const backoffMs = REGISTRATION_RETRY_BACKOFF_MS[attempt];
          if (backoffMs === undefined) throw error;
          logger.warn('[AudioLevels] listen() failed, retrying', {
            attempt: attempt + 1,
            backoffMs,
          });
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
        }
      }
    } catch (error) {
      logger.error('[AudioLevels] Failed to register listener', error);
    } finally {
      registrationInFlight = false;
    }
  })();
}

/**
 * Invoke a managed release and RETAIN THE HANDLE for one more attempt.
 *
 * `unlisten` itself is still cleared synchronously by the caller, and that
 * matters: the claim path gates re-registration on `!unlisten`, so holding the
 * handle there until the release resolved would make a consumer that mounts
 * during the teardown window register nothing at all. It would not throw and it
 * would not warn — the meters would simply stop for that consumer. The retained
 * copy lives outside that gate for exactly that reason.
 */
function releaseHandle(handle: ManagedUnlistenFn | null): void {
  if (!handle) return;
  staleRelease = handle;
  void handle();
}

function teardown(): void {
  flush?.cancel();
  flush = null;
  latestBySource = {};
  // Fire-and-forget by design, but no longer fire-and-FORGET-THE-FAILURE: the
  // managed unlisten owns the retry and the tombstone, so a rejection here
  // cannot leave a callback that Rust keeps delivering to. `teardown()` stays
  // synchronous because every caller is a React cleanup.
  releaseHandle(unlisten);
  unlisten = null;
  // Drop the last frame so a later remount can't paint a stale level before
  // the first live one arrives.
  getDefaultStore().set(tauriAudioLevelsAtom, undefined);
}

/**
 * Claim a share of the `AUDIO_LEVELS` listener. Returns a release function;
 * the listener is torn down when the last holder releases.
 *
 * Safe to call from any number of consumers in the same webview — they share a
 * single underlying listener. Outside Tauri it is a no-op.
 */
export function subscribeToAudioLevels(): () => void {
  if (!isTauri()) return () => {};

  subscriberCount += 1;
  if (subscriberCount === 1) {
    latestBySource = {};
    flush = createFlush();
  }

  // Spend the retained release, if there is one. A mount is the only moment
  // this module gets to re-attempt a denied `plugin:event|unlisten`, and it is
  // a no-op when the earlier release succeeded. Before `register()` only for
  // readability — the two concern different listeners and do not interact.
  if (staleRelease) {
    const retry = staleRelease;
    staleRelease = null;
    void retry();
  }

  // Outside the 0->1 branch on purpose: if `register()` exhausted its retries,
  // this is the second line of defence, so a later mount re-attempts rather
  // than every live consumer staying flat. No-op once a handle exists.
  if (!unlisten && !registrationInFlight) register();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    subscriberCount -= 1;
    if (subscriberCount === 0) teardown();
  };
}
